import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBrands } from "@/lib/api";
import { BrandBadgeOverlay } from "@/components/BrandBadges";
import { optimizedImage, optimizedSrcSet } from "@/lib/cloudinaryImage";
import { brandOpenState, sortBrandsByAvailability } from "@/lib/brandAvailability";

const BrandsIndex = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBrands().then((b) => { setBrands(sortBrandsByAvailability(b)); setLoading(false); });
    }, []);

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="brands-page">
            <header className="border-b border-[var(--border)]">
                <div className="container-nk py-5 flex items-center justify-between">
                    <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold">
                        ← naami<span className="text-gold">.</span> hub
                    </Link>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                        // QR Menus
                    </span>
                </div>
            </header>

            <section className="container-nk py-14">
                <p className="eyebrow">// Meet the Brands</p>
                <h1 className="font-display text-[44px] md:text-[64px] leading-[0.95] mt-3 mb-3">
                    Scan a code, or <span className="text-gold">pick below.</span>
                </h1>
                <p className="text-muted text-[15px] max-w-md font-light leading-relaxed mb-12">
                    Each brand has its own mobile menu. Tap a card, browse the dishes,
                    place an order — collect at the counter.
                </p>

                {loading ? (
                    <p className="text-muted">Loading brands…</p>
                ) : (
                    <div
                        className="grid gap-4"
                        data-testid="brands-grid"
                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
                    >
                        {brands.map((b) => {
                            const avail = brandOpenState(b);
                            // Only `coming_soon` blocks the tile. Closed-for-now
                            // brands stay clickable so customers can browse the
                            // menu and place an order for later.
                            const blocked = !avail.available && (avail.reason === "coming_soon" || avail.reason === "closed_now");
                            const CardTag = blocked ? "div" : Link;
                            return (
                            <CardTag
                                key={b.id}
                                {...(blocked ? {} : { to: `/m/${b.slug}` })}
                                data-testid={`brand-link-${b.slug}`}
                                className={`brand-card ${blocked ? "opacity-70 cursor-not-allowed" : ""}`}
                                style={{ "--brand-color": b.accent }}
                            >
                                <div className="photo">
                                    {b.image_url && <img src={optimizedImage(b.image_url, { w: 320 })} srcSet={optimizedSrcSet(b.image_url, 320)} alt={`${b.name} dish`} loading="lazy" style={blocked ? { filter: "grayscale(0.8)" } : undefined} />}
                                    {b.halal && <span className="halal-badge">Halal</span>}
                                    <BrandBadgeOverlay badges={b.badges} etaMin={b.eta_min} layout="card" />
                                    {!avail.available && (
                                        <div
                                            className="absolute inset-x-0 top-3 flex justify-center pointer-events-none px-2"
                                            data-testid={`brand-status-${b.slug}`}
                                        >
                                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[var(--bg)] border border-[var(--gold)] text-gold text-center leading-tight max-w-full">
                                                {avail.reason === "coming_soon" ? "Opening soon" : avail.nextOpenLabel}
                                            </span>
                                        </div>
                                    )}
                                    <div className="photo-overlay" aria-hidden />
                                    <div className="stripe" aria-hidden />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-3.5">
                                        <div className="logo-box">{b.token}</div>
                                        <div className="min-w-0 flex-1">
                                            <h3
                                                className="font-display text-[24px] leading-tight truncate"
                                                style={{ color: b.accent }}
                                            >
                                                {b.name}
                                            </h3>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted truncate mt-0.5">
                                                {b.tagline}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-5">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                            {b.cuisine}
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                                            {blocked ? (avail.reason === "closed_now" ? "Closed right now" : "Opening soon") : avail.available ? "Open Menu →" : "Pre-order for later →"}
                                        </span>
                                    </div>
                                </div>
                            </CardTag>
                        );})}
                    </div>
                )}
            </section>
        </div>
    );
};

export default BrandsIndex;
